import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Search, Download, Eye, Trash2, CheckCircle, XCircle,
  Calendar, Phone, Mail, Filter, ChevronDown, ChevronUp,
  AlertTriangle, RefreshCw, ArrowLeft, UserCheck, Clock
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Demo registrations for testing when DB is unavailable
const DEMO_REGISTRATIONS = [
  {
    id: 'demo-reg-001',
    registration_number: 'KCC-2026-001',
    status: 'confirmed',
    total_amount: 1500,
    amount_paid: 1500,
    payment_status: 'paid',
    created_at: '2026-08-10T14:30:00',
    contact_email: 'john.doe@example.com',
    contact_phone: '+254712345678',
    participants: [
      { id: 'p1', first_name: 'John', last_name: 'Doe', participant_type: 'primary', team: 'Golfers', age: 42, gender: 'male' },
      { id: 'p2', first_name: 'Jane', last_name: 'Doe', participant_type: 'adult', team: 'Golfers', age: 38, gender: 'female' },
      { id: 'p3', first_name: 'Tom', last_name: 'Doe', participant_type: 'junior', team: 'Juniors', age: 12, gender: 'male' },
    ]
  },
  {
    id: 'demo-reg-002',
    registration_number: 'KCC-2026-002',
    status: 'pending',
    total_amount: 500,
    amount_paid: 0,
    payment_status: 'pending',
    created_at: '2026-08-11T09:15:00',
    contact_email: 'mary.smith@example.com',
    contact_phone: '+254723456789',
    participants: [
      { id: 'p4', first_name: 'Mary', last_name: 'Smith', participant_type: 'primary', team: 'Gymkhana', age: 35, gender: 'female' },
      { id: 'p5', first_name: 'Guest', last_name: 'Child', participant_type: 'guest_child', team: 'Juniors', age: 8, gender: 'male' },
    ]
  },
  {
    id: 'demo-reg-003',
    registration_number: 'KCC-2026-003',
    status: 'confirmed',
    total_amount: 0,
    amount_paid: 0,
    payment_status: 'free',
    created_at: '2026-08-11T11:45:00',
    contact_email: 'peter.kamau@example.com',
    contact_phone: '+254734567890',
    participants: [
      { id: 'p6', first_name: 'Peter', last_name: 'Kamau', participant_type: 'primary', team: 'Karenites', age: 45, gender: 'male' },
      { id: 'p7', first_name: 'Grace', last_name: 'Kamau', participant_type: 'adult', team: 'Karenites', age: 40, gender: 'female' },
      { id: 'p8', first_name: 'David', last_name: 'Kamau', participant_type: 'junior', team: 'Juniors', age: 16, gender: 'male' },
      { id: 'p9', first_name: 'Sarah', last_name: 'Kamau', participant_type: 'junior', team: 'Juniors', age: 14, gender: 'female' },
    ]
  }
];

// Team colors for badges
const teamColors = {
  'Golfers': 'bg-green-100 text-green-700',
  'Gymkhana': 'bg-blue-100 text-blue-700',
  'Karenites': 'bg-red-100 text-red-700',
  'Juniors': 'bg-amber-100 text-amber-700',
};

// Participant type labels
const participantTypeLabels = {
  'primary': 'Primary Member',
  'adult': 'Adult Family',
  'junior': 'Junior',
  'guest_child': 'Guest Child',
  'guest_adult': 'Guest Adult',
  'guest': 'Guest',
};

// Status badge styles
const statusStyles = {
  'confirmed': 'bg-green-100 text-green-700',
  'pending': 'bg-yellow-100 text-yellow-700',
  'cancelled': 'bg-red-100 text-red-700',
  'paid': 'bg-green-100 text-green-700',
  'free': 'bg-gray-100 text-gray-700',
};

export default function AdminRegistrationsPage() {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [demoMode, setDemoMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [expandedReg, setExpandedReg] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    confirmed: 0,
    pending: 0,
    totalParticipants: 0,
    totalRevenue: 0
  });

  // Fetch registrations
  const fetchRegistrations = useCallback(async () => {
    setLoading(true);
    
    try {
      // First get events to find the KCC event
      const eventsRes = await fetch(`${API}/api/events`);
      
      if (!eventsRes.ok) {
        throw new Error('Failed to fetch events');
      }
      
      const events = await eventsRes.json();
      const kccEvent = events.find(e => 
        e.name?.includes('KCC') || e.name?.includes('Family Sports Day')
      );
      
      if (kccEvent) {
        // Fetch registrations for this event
        const regRes = await fetch(`${API}/api/events/${kccEvent.id}/registrations`);
        
        if (regRes.ok) {
          const regData = await regRes.json();
          setRegistrations(regData);
          calculateStats(regData);
          setDemoMode(false);
          setLoading(false);
          return;
        }
      }
      
      // If we get here, use demo data
      throw new Error('No registrations found');
      
    } catch (error) {
      console.error('Error fetching registrations:', error);
      // Use demo data
      setRegistrations(DEMO_REGISTRATIONS);
      calculateStats(DEMO_REGISTRATIONS);
      setDemoMode(true);
    }
    
    setLoading(false);
  }, []);

  // Calculate statistics
  const calculateStats = (regs) => {
    const totalParticipants = regs.reduce((sum, r) => sum + (r.participants?.length || 0), 0);
    const totalRevenue = regs.reduce((sum, r) => sum + (r.amount_paid || 0), 0);
    
    setStats({
      total: regs.length,
      confirmed: regs.filter(r => r.status === 'confirmed').length,
      pending: regs.filter(r => r.status === 'pending').length,
      totalParticipants,
      totalRevenue
    });
  };

  useEffect(() => {
    fetchRegistrations();
  }, [fetchRegistrations]);

  // Filter registrations
  const filteredRegistrations = registrations.filter(reg => {
    // Search filter
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery || 
      reg.registration_number?.toLowerCase().includes(searchLower) ||
      reg.contact_email?.toLowerCase().includes(searchLower) ||
      reg.contact_phone?.includes(searchQuery) ||
      reg.participants?.some(p => 
        `${p.first_name} ${p.last_name}`.toLowerCase().includes(searchLower)
      );
    
    // Status filter
    const matchesStatus = statusFilter === 'all' || reg.status === statusFilter;
    
    // Team filter
    const matchesTeam = teamFilter === 'all' || 
      reg.participants?.some(p => p.team === teamFilter);
    
    return matchesSearch && matchesStatus && matchesTeam;
  });

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Registration #', 'Status', 'Contact Email', 'Contact Phone', 'Total Amount', 'Amount Paid', 'Created', 'Participant Name', 'Type', 'Team', 'Age', 'Gender'];
    
    const rows = [];
    filteredRegistrations.forEach(reg => {
      reg.participants?.forEach((p, idx) => {
        rows.push([
          idx === 0 ? reg.registration_number : '',
          idx === 0 ? reg.status : '',
          idx === 0 ? reg.contact_email : '',
          idx === 0 ? reg.contact_phone : '',
          idx === 0 ? reg.total_amount : '',
          idx === 0 ? reg.amount_paid : '',
          idx === 0 ? new Date(reg.created_at || reg.registration_date).toLocaleDateString() : '',
          `${p.first_name} ${p.last_name}`,
          participantTypeLabels[p.participant_type] || p.participant_type,
          p.team || '',
          p.age || '',
          p.gender || ''
        ]);
      });
    });
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kcc-registrations-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success('Registrations exported to CSV');
  };

  // Get unique teams from participants
  const allTeams = [...new Set(
    registrations.flatMap(r => r.participants?.map(p => p.team).filter(Boolean) || [])
  )];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => window.history.back()}
                className="gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Event Registrations</h1>
                <p className="text-sm text-gray-500">KCC Family Sports Day 2026</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={fetchRegistrations}
                className="gap-2"
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                onClick={exportToCSV}
                className="bg-green-600 hover:bg-green-700 gap-2"
                disabled={filteredRegistrations.length === 0}
              >
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Demo Mode Warning */}
        {demoMode && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800">Demo Mode Active</p>
              <p className="text-sm text-yellow-700">
                Database connection unavailable. Showing sample registration data for preview.
              </p>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-xs text-gray-500">Total Registrations</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.confirmed}</p>
                <p className="text-xs text-gray-500">Confirmed</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
                <p className="text-xs text-gray-500">Pending</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <UserCheck className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.totalParticipants}</p>
                <p className="text-xs text-gray-500">Participants</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <span className="text-emerald-600 font-bold text-sm">KES</span>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.totalRevenue.toLocaleString()}</p>
                <p className="text-xs text-gray-500">Revenue</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 border shadow-sm mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, phone, or registration #..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                data-testid="registration-search"
              />
            </div>
            
            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="px-3 py-2 border rounded-lg text-gray-900 focus:ring-2 focus:ring-green-500"
                data-testid="status-filter"
              >
                <option value="all">All Status</option>
                <option value="confirmed">Confirmed</option>
                <option value="pending">Pending</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            
            {/* Team Filter */}
            <div>
              <select
                value={teamFilter}
                onChange={e => setTeamFilter(e.target.value)}
                className="px-3 py-2 border rounded-lg text-gray-900 focus:ring-2 focus:ring-green-500"
                data-testid="team-filter"
              >
                <option value="all">All Teams</option>
                {allTeams.map(team => (
                  <option key={team} value={team}>{team}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Registrations List */}
        <div className="space-y-4">
          {loading ? (
            <div className="bg-white rounded-xl p-12 border shadow-sm text-center">
              <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
              <p className="text-gray-500">Loading registrations...</p>
            </div>
          ) : filteredRegistrations.length === 0 ? (
            <div className="bg-white rounded-xl p-12 border shadow-sm text-center">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">No registrations found</p>
              <p className="text-sm text-gray-400 mt-1">
                {searchQuery || statusFilter !== 'all' || teamFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Registrations will appear here once people sign up'}
              </p>
            </div>
          ) : (
            filteredRegistrations.map(reg => (
              <motion.div
                key={reg.id}
                layout
                className="bg-white rounded-xl border shadow-sm overflow-hidden"
              >
                {/* Registration Header */}
                <div 
                  className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedReg(expandedReg === reg.id ? null : reg.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <Users className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-gray-900">{reg.registration_number}</p>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusStyles[reg.status] || 'bg-gray-100 text-gray-600'}`}>
                            {reg.status}
                          </span>
                          {reg.payment_status && reg.payment_status !== reg.status && (
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusStyles[reg.payment_status] || 'bg-gray-100 text-gray-600'}`}>
                              {reg.payment_status}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          {reg.participants?.length || 0} participants • 
                          Created {new Date(reg.created_at || reg.registration_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">
                          {reg.total_amount === 0 ? 'FREE' : `KES ${reg.total_amount?.toLocaleString()}`}
                        </p>
                        {reg.total_amount > 0 && (
                          <p className="text-xs text-gray-500">
                            Paid: KES {reg.amount_paid?.toLocaleString() || 0}
                          </p>
                        )}
                      </div>
                      {expandedReg === reg.id ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Expanded Details */}
                <AnimatePresence>
                  {expandedReg === reg.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t"
                    >
                      <div className="p-4 bg-gray-50">
                        {/* Contact Info */}
                        <div className="flex flex-wrap gap-4 mb-4">
                          {reg.contact_email && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Mail className="w-4 h-4" />
                              <a href={`mailto:${reg.contact_email}`} className="hover:text-green-600">
                                {reg.contact_email}
                              </a>
                            </div>
                          )}
                          {reg.contact_phone && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Phone className="w-4 h-4" />
                              <a href={`tel:${reg.contact_phone}`} className="hover:text-green-600">
                                {reg.contact_phone}
                              </a>
                            </div>
                          )}
                        </div>
                        
                        {/* Participants Table */}
                        <div className="bg-white rounded-lg border overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-4 py-2 text-left font-medium text-gray-600">Name</th>
                                <th className="px-4 py-2 text-left font-medium text-gray-600">Type</th>
                                <th className="px-4 py-2 text-left font-medium text-gray-600">Team</th>
                                <th className="px-4 py-2 text-left font-medium text-gray-600">Age</th>
                                <th className="px-4 py-2 text-left font-medium text-gray-600">Gender</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {reg.participants?.map(p => (
                                <tr key={p.id} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 font-medium text-gray-900">
                                    {p.first_name} {p.last_name}
                                  </td>
                                  <td className="px-4 py-3 text-gray-600">
                                    {participantTypeLabels[p.participant_type] || p.participant_type}
                                  </td>
                                  <td className="px-4 py-3">
                                    {p.team && (
                                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${teamColors[p.team] || 'bg-gray-100 text-gray-600'}`}>
                                        {p.team}
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-gray-600">{p.age || '-'}</td>
                                  <td className="px-4 py-3 text-gray-600 capitalize">{p.gender || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
